import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
	compose,
	withStateHandlers,
	withPropsOnChange,
	branch,
	renderComponent,
} from 'recompose';
import withCookies from 'react-cookie/lib/withCookies';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { includes, isArray, get } from 'lodash';

import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';

import { metaPropTypes, phonePropType } from 'config/propTypes';
import { withSearchHandler, withErrorHandler } from 'decorators';
import { withSearchPageHandlers } from 'decorators/search';

import { H2 } from 'components/core/Typography';
import { LoadingPage } from 'components/core/LoadingPage';
import { LazyHydrate } from 'components/core/LazyHydrate';
import { Page } from 'components/core/Page';
import { SearchContent } from 'components/search/SearchContent';
import { SearchResults } from 'components/search/SearchResults';
import { PaginationSummary } from 'components/search/PaginationSummary';
import { SearchPagination } from 'components/search/SearchPagination';
import { SearchToolBar } from 'components/search/SearchToolBar';
import { SearchFiltersModal } from 'components/search/SearchFiltersModal';
import { SearchMap } from 'components/search/SearchMap';
import { SearchTray } from 'components/search/SearchTray';

import searchSelector from 'store/selectors/search';
import {
	getSearchResults,
	showSearchFilters,
	hideSearchFilters,
	updateSearchFilters,
} from 'store/modules/search';
import { withPageViewTracking } from 'components/core/library/tracking-utils';

import defaultUnitFilters from 'config/filters';

import './SearchPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { INTERNAL_PAGE_TYPES } from '../../config/constants';
import { withExperiment } from '@sparefoot/react-core/decorators';
import { toTitleCase } from '../../utils/formatting';

export const enhance = compose(
	withCookies,
	connect(
		searchSelector,
		{
			getSearchResults,
			hideSearchFilters,
			showSearchFilters,
			updateSearchFilters,
		},
		mapDispatchToActions,
	),
	withErrorHandler,
	withSearchHandler,
	withSearchPageHandlers,
	branch(
		(props) => props.loading && !props.searchId,
		renderComponent(LoadingPage),
	),
	withPageViewTracking(PAGE_TYPES.SEARCH),
	withExperiment(
		'sf_15810_ssdc_sticky_click_to_call_revisit',
		// Only log the impression if mobile
		(props) =>
			props?.withinCallCenterHours &&
			props?.deviceType &&
			props.deviceType !== 'desktop',
	),
	withStateHandlers(
		() => ({
			facilitySelected: null, // this is the facility id of the facility that is selected
			viewListMap: true,
			viewFilter: false, // this is to open the filter
			countFilter: { count: 0, categorySelect: {} }, // This is the object for counting the selected filters
			defaultFilters: defaultUnitFilters, // This is the object of the default structure of the filters
			pinSelected: false, // this is the state of the pin on the map
		}),
		{
			handleFacilitySelected: () => (facilityId) => ({
				facilitySelected: facilityId,
			}),
			handleFacilityDeselected: () => () => ({ facilitySelected: null }),
			handlePinSelected: () => () => ({ pinSelected: true }),
			handlePinDeselected: () => () => ({ pinSelected: false }),
			handleViewListMap:
				({ viewListMap }) =>
				() => {
					scrollToElement('#search-page-results', { offset: -138 }); // sticky search height
					return {
						viewListMap: !viewListMap,
					};
				},
			handleViewFilter:
				({ viewFilter }) =>
				() => ({
					viewFilter: !viewFilter,
				}),
			// This method counts the filters, it has two conditions: filters that can be multiple or not
			handleCountFilter:
				({ countFilter }) =>
				(allowMultiple, checked, name) => {
					const updatedCountFilter = { ...countFilter };
					if (checked && allowMultiple) {
						updatedCountFilter.count += 1;
					} else if (
						checked &&
						!allowMultiple &&
						!countFilter.categorySelect[name]
					) {
						updatedCountFilter.count += 1;
						updatedCountFilter.categorySelect[name] = true;
					}
					return { countFilter: updatedCountFilter };
				},
			// this method resets the filter count for when the location or type of filter changes
			handleCounterFilterReset:
				({ countFilter }) =>
				() => {
					const updatedCountFilter = { ...countFilter };
					updatedCountFilter.count = 0;
					updatedCountFilter.categorySelect = {};
					return { countFilter: updatedCountFilter };
				},
		},
	),
	// This method is activated every time the location changes, it will count the filters
	withPropsOnChange(
		['searchLocation'],
		({
			searchParams,
			searchType,
			handleCountFilter,
			defaultFilters,
			handleCounterFilterReset,
			searchLocation,
		}) => {
			handleCounterFilterReset();
			if (!searchLocation) return;
			const filters = defaultFilters[searchType];
			Object.keys(filters).forEach((filterName) => {
				const filter = filters[filterName];
				if (filter.options) {
					filter.options.forEach((option) => {
						const isChecked =
							searchParams[option.name] === `${option.value}` ||
							(isArray(searchParams[option.name]) &&
								includes(
									searchParams[option.name],
									`${option.value}`,
								));
						if (isChecked) {
							handleCountFilter(
								filter.multiple,
								isChecked,
								option.name,
							);
						}
					});
				}
			});
		},
	),
);

export function SearchPage(props) {
	const {
		errorMessage,
		facilities,
		baseUrl,
		cloudfrontUrl,
		facilitySelected,
		faqContent,
		faqShowing,
		handleFacilityDeselected,
		handleFacilitySelected,
		handlePinDeselected,
		handlePinSelected,
		listingsPerPage,
		loading,
		meta,
		page,
		pageType,
		phone,
		searchContent,
		searchLocation,
		searchParams,
		onSearch,
		pinSelected,
		searchType,
		showInformedSearch,
		showRecommendedUnit,
		totalListings,
		totalPages,
		trackingPixel,
		handleSearch,
		viewListMap,
		handleViewListMap,
		handleViewFilter,
		facetCounts,
		storageTypeUrls,
		viewFilter,
		countFilter,
		visitId,
		searchId,
		stickyTrayPhoneNumber,
		sf15810Variation,
		withinCallCenterHours,
		staticMapUrl,
		cityUrl,
		stateUrl,
	} = props;

	const {
		city,
		state: stateAbbreviation,
		subPageType,
		vehicle,
	} = searchParams;
	const titleCaseCity = city ? toTitleCase(city) : '';
	const storageTypeUrlsSafe =
		pageType == INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE
			? storageTypeUrls?.vehicle
			: storageTypeUrls?.storage;
	const landingPageUrl = `${baseUrl}${storageTypeUrlsSafe}${subPageType ? subPageType : ''}`;

	let amenityType =
		pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE && city
			? ''
			: pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE &&
				  subPageType
				? subPageType
				: pageType === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE &&
					  vehicle
					? vehicle
					: '';

	const shouldRenderSchema =
		(pageType === 'cityLandingPage' || pageType === 'amenityLandingPage') &&
		page == 1;

	return (
		<Page
			className="search-page"
			trackingPixel={trackingPixel}
			loading={loading}
			pageType={pageType}
			title={meta.title}
			description={meta.description}
			noIndex={meta.noindex}
			noFollow={meta.nofollow}
			prev={meta.prev}
			next={meta.next}
			canonical={meta.canonical}
		>
			{(pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
				pageType === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE ||
				pageType === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE) && (
				<Helmet>
					<script type="application/ld+json">
						{`
							{
                            "@context": "https://schema.org",
                            "@type": "WebSite",
                              "name": "http://SelfStorage.com",
  							  "url": "https://www.selfstorage.com",
                            "potentialAction": {
                                "@type": "SearchAction",
                                "target": {
									"@type": "EntryPoint",
									"urlTemplate": "https://www.selfstorage.com/self-storage/${stateAbbreviation}/${city}",
									"inLanguage": "en-US",
									"actionPlatform": [
										"http://schema.org/DesktopWebPlatform ",
										"http://schema.org/MobileWebPlatform "
									]
								},
                                    "query-input": {
										"@type": "PropertyValueSpecification",
										"valueRequired": "http://schema.org/False",
										"valueName": "location"
									}
								}
                            }
                            }
								{
								"@context": "https://schema.org",
								"@type": "WebPage",
								"name": "${titleCaseCity}] Self Storage",
								"url": "${landingPageUrl}",
								"description": "${meta.description}",
								"mainEntity": {
			  						"@type": "Place",
			  						"name": "${titleCaseCity}, ${searchLocation?.state}",
			  					"hasMap": {
										"@type": "Map",
										"name": "${titleCaseCity} Self Storage Map",
										"url": "${staticMapUrl}"
									}
								  }
								}
						`}
					</script>
				</Helmet>
			)}
			<SearchToolBar
				handleSearch={handleSearch}
				location={searchLocation}
				viewListMap={viewListMap}
				handleViewListMap={handleViewListMap}
				sticky
				handleViewFilter={handleViewFilter}
				countFilter={countFilter.count}
				vehicle={vehicle}
				amenityType={amenityType}
			/>
			{viewFilter && (
				<SearchFiltersModal
					title="Filter your search"
					showing={viewFilter}
					onClose={handleViewFilter}
					facetCounts={facetCounts}
					onFilter={onSearch}
					searchParams={searchParams}
					searchType={searchType}
					storageTypeUrls={storageTypeUrls}
				/>
			)}

			{sf15810Variation === 'variation' && (
				<SearchTray
					delay={10000}
					visitId={visitId}
					searchId={searchId}
					searchLocation={searchLocation}
					stickyTrayPhoneNumber={stickyTrayPhoneNumber}
					onShowSearchFilterModal={handleViewFilter}
					withinCallCenterHours={withinCallCenterHours}
				/>
			)}

			<LazyHydrate
				id="lazy-search-page"
				noLazy={!showInformedSearch}
			>
				<div className="search-results-container">
					<SearchMap
						className={classnames({
							'display-view-list-map': viewListMap,
						})}
						facilities={facilities}
						baseUrl={baseUrl}
						cloudfrontUrl={cloudfrontUrl}
						facilitySelected={facilitySelected}
						handleFacilityDeselected={handleFacilityDeselected}
						handleFacilitySelected={handleFacilitySelected}
						handlePinDeselected={handlePinDeselected}
						handlePinSelected={handlePinSelected}
						handleSearch={handleSearch}
						location={searchLocation}
						mapShowing
						pinSelected={pinSelected}
						searchType={searchType}
						searchParams={searchParams}
						errorMessage={errorMessage}
						staticMapUrl={staticMapUrl}
					/>
					<div
						id="search-page-results"
						className={classnames('results-container', {
							'display-view-list-map': !viewListMap,
						})}
					>
						<div className="results-n-pagination">
							{searchLocation && (
								<H2
									size="smallMedium"
									weight="standard"
									color="darkGray"
									className="title-results-pagination"
									last
								>
									<PaginationSummary
										listingsPerPage={listingsPerPage}
										pageListingsCount={facilities.length}
										page={page}
										totalItems={totalListings}
										amenityType={amenityType}
										city={searchLocation.city}
										state={searchLocation.state}
									/>
								</H2>
							)}
							{/* {searchLocation.city === 'Los Angeles' && (
								<AlertBanner
									theme="warning"
									icon={<FireIcon />}
									title="Important Alert"
									message="Due to the fires in Los Angeles, CA area, some facilities may not have units available at this time. Please call (844) 611-1659 for assistance."
								/>
							)} */}
							<SearchResults
								error={errorMessage}
								facilities={facilities}
								baseUrl={baseUrl}
								cloudfrontUrl={cloudfrontUrl}
								facilitySelected={facilitySelected}
								handleFacilityDeselected={
									handleFacilityDeselected
								}
								handleFacilitySelected={handleFacilitySelected}
								handlePinDeselected={handlePinDeselected}
								handlePinSelected={handlePinSelected}
								isCLP={shouldRenderSchema}
								loading={loading}
								location={searchLocation}
								phone={phone}
								searchType={searchType}
								showRecommendedUnit={showRecommendedUnit}
								searchParams={searchParams}
							/>
							<SearchPagination
								numPages={totalPages}
								searchParams={searchParams}
								selectedPage={page}
							/>
						</div>
						{searchContent &&
							searchLocation &&
							(pageType ===
								INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE ||
								pageType ===
									INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE) && (
								<SearchContent
									pageType={pageType}
									location={searchLocation}
									content={searchContent}
									searchParams={searchParams}
									faqContent={faqContent}
									faqShowing={faqShowing}
									amenityType={amenityType}
									cityUrl={cityUrl}
									stateUrl={stateUrl}
									baseUrl={baseUrl}
									searchType={searchType}
									averageMonthlyUnitPrice={get(
										searchContent,
										'averageMonthlyUnitPrice',
									)}
								/>
							)}
					</div>
				</div>
			</LazyHydrate>
		</Page>
	);
}

SearchPage.propTypes = {
	errorMessage: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	baseUrl: PropTypes.string,
	cloudfrontUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinDeselected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	listingsPerPage: PropTypes.number,
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	onSearch: PropTypes.func,
	page: PropTypes.number,
	pageType: PropTypes.string,
	phone: phonePropType,
	pinSelected: PropTypes.bool,
	searchContent: PropTypes.object,
	searchLocation: PropTypes.object,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	showInformedSearch: PropTypes.bool,
	showRecommendedUnit: PropTypes.bool,
	totalListings: PropTypes.number,
	totalPages: PropTypes.number,
	trackingPixel: PropTypes.string.isRequired,
	handleSearch: PropTypes.func.isRequired,
	viewListMap: PropTypes.bool,
	handleViewListMap: PropTypes.func,
	handleViewFilter: PropTypes.func,
	facetCounts: PropTypes.object,
	storageTypeUrls: PropTypes.shape({
		storage: PropTypes.string,
		vehicle: PropTypes.string,
	}),
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	viewFilter: PropTypes.bool,
	countFilter: PropTypes.object,
	visitId: PropTypes.string,
	searchId: PropTypes.string,
	stickyTrayPhoneNumber: PropTypes.object,
	sf15810Variation: PropTypes.string,
	withinCallCenterHours: PropTypes.bool,
	staticMapUrl: PropTypes.string,
};

export default enhance(SearchPage);
